import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faLocationDot, faPhone, faEnvelope, faClock, faUser, faHeart, faHandshake, faStar, faCheckCircle, faUtensils, faTree, faCouch, faRestroom, faBed, faUserMd, faStethoscope, faPills, faSoap, faMusic, faDumbbell, faPalette, faShield, faMagnifyingGlass, faArrowLeft, faArrowRight, faTimes, faSpinner, faArrowRight as faArrow, faCookie, faFileContract, faGear } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './App.css';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const galleryImages = [
    { src: "/images/New-images/ResidencialBlanes-02.jpg", alt: "Instalaciones 1" },
    { src: "/images/New-images/ResidencialBlanes-05.jpg", alt: "Instalaciones 2" },
    { src: "/images/New-images/ResidencialBlanes-07.jpg", alt: "Instalaciones 3" },
    { src: "/images/New-images/ResidencialBlanes-08.jpg", alt: "Instalaciones 4" },
    { src: "/images/New-images/ResidencialBlanes-09.jpg", alt: "Instalaciones 5" },
    { src: "/images/New-images/ResidencialBlanes-10.jpg", alt: "Instalaciones 6" },
    { src: "/images/New-images/ResidencialBlanes-13 (1).jpg", alt: "Instalaciones 7" },
    { src: "/images/New-images/ResidencialBlanes-13.jpg", alt: "Instalaciones 8" },
    { src: "/images/New-images/ResidencialBlanes-24.jpg", alt: "Instalaciones 9" },
    { src: "/images/New-images/ResidencialBlanes-34.jpg", alt: "Instalaciones 10" },
    { src: "/images/New-images/ResidencialBlanes-62.jpg", alt: "Instalaciones 11" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          
          // Add staggered animation for child elements
          const children = entry.target.querySelectorAll('.animate-item');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('animate-in');
            }, index * 100);
          });
        }
      });
    }, observerOptions);

    // Observe all sections and animated elements
    const sections = document.querySelectorAll('section');
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    
    sections.forEach((section) => observer.observe(section));
    animatedElements.forEach((element) => observer.observe(element));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
      animatedElements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [modalOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      
      setMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const handleKeyDown = (e) => {
    if (modalOpen) {
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  // Cookie consent management
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent === null) {
      setShowCookieBanner(true);
    } else {
      setCookieConsent(consent === 'true');
    }
  }, []);

  const acceptCookies = () => {
    setCookieConsent(true);
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieBanner(false);
  };

  const rejectCookies = () => {
    setCookieConsent(false);
    localStorage.setItem('cookieConsent', 'false');
    setShowCookieBanner(false);
  };

  const openTermsModal = () => {
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  // Close mobile menu on escape key and prevent body scroll when open
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      // Check if screen width is greater than 768px (desktop breakpoint)
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        message: formData.message,
        to_email: 'your-email@gmail.com' // Replace with your Gmail address
      };


      // Send email using EmailJS
      const result = await emailjs.send(
        'belmangiare', // Replace with your EmailJS service ID
        'template_ktyhmfk', // Replace with your EmailJS template ID
        templateParams,
        '0mGtb4M2aFPwut2D0' // Replace with your EmailJS public key
      );
      console.log('Email sent successfully:', result);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      
      // Reset error status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="logo">
          <h1>Residencial Blanes</h1>
          </div>
          
          {/* Hamburger Menu Button */}
          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Desktop Navigation */}
          <nav className="nav desktop-nav">
            <ul>
              <li><a href="#home" onClick={() => scrollToSection('home')}>Inicio</a></li>
              <li><a href="#about" onClick={() => scrollToSection('about')}>Nosotros</a></li>
              <li><a href="#facilities" onClick={() => scrollToSection('facilities')}>Instalaciones</a></li>
              <li><a href="#services" onClick={() => scrollToSection('services')}>Servicios</a></li>
              <li><a href="#testimonials" onClick={() => scrollToSection('testimonials')}>Testimonios</a></li>
              <li><a href="#faq" onClick={() => scrollToSection('faq')}>FAQ</a></li>
              <li><a href="#contact" onClick={() => scrollToSection('contact')}>Contacto</a></li>
            </ul>
          </nav>

        </div>
      </header>

      {/* Mobile Navigation - Moved outside header */}
      <nav 
        className={`mobile-nav ${mobileMenuOpen ? 'active' : ''} ${isScrolled ? 'scrolled' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setMobileMenuOpen(false);
          }
        }}
      >
        <ul>
          <li><a href="#home" onClick={() => scrollToSection('home')}>Inicio</a></li>
          <li><a href="#about" onClick={() => scrollToSection('about')}>Nosotros</a></li>
          <li><a href="#facilities" onClick={() => scrollToSection('facilities')}>Instalaciones</a></li>
          <li><a href="#services" onClick={() => scrollToSection('services')}>Servicios</a></li>
          <li><a href="#testimonials" onClick={() => scrollToSection('testimonials')}>Testimonios</a></li>
          <li><a href="#faq" onClick={() => scrollToSection('faq')}>FAQ</a></li>
          <li><a href="#contact" onClick={() => scrollToSection('contact')}>Contacto</a></li>
        </ul>
      </nav>

      <main>
        <section id="home" className="hero">
          <video autoPlay muted loop playsInline>
            <source src="/videos/hero-video-light.mp4" type="video/mp4" />
            <source src="/videos/hero-video.webm" type="video/webm" />
            Your browser does not support the video tag.
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <div className="hero-text-block">
              <img src="/images/blanes-logo-final.png" alt="Residencial Blanes" className="hero-logo" />
              <p>Tu tranquilidad es la nuestra.</p>

              <div className="hero-buttons">
                <button className="cta-button hero-button primary" onClick={() => scrollToSection('contact')}>Agendá tu visita</button>
                <div className="hero-location">
                <FontAwesomeIcon icon={faLocationDot} className="location-icon" />
                <span>Montevideo, Uruguay</span>
              </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="about">
          <div className="container">
            <div className="about-header animate-on-scroll">
              <div className="about-badge animate-item">
                <FontAwesomeIcon icon={faHome} className="badge-icon" />
                <span>Más de 10 años cuidando</span>
              </div>
              <h2 className="animate-item">¿Quiénes somos?</h2>
              <p className="about-subtitle animate-item">Una familia dedicada al bienestar de los adultos mayores</p>
            </div>
            
            <div className="about-content">
              <div className="about-text">
                <div className="about-story animate-on-scroll">
                  <div className="story-item animate-item">
                    <div className="story-icon">
                      <FontAwesomeIcon icon={faStar} />
                    </div>
                    <div className="story-content">
                      <h3>Nuestro propósito</h3>
                      <p>
                        En Residencial Blanes abrimos nuestras puertas hace más de 10 años con un único propósito: ofrecer un hogar real y humano para los adultos mayores que necesitan compañía, cuidado y respeto en esta etapa de su vida.
                      </p>
                    </div>
                  </div>
                  
                  <div className="story-item animate-item">
                    <div className="story-icon">
                      <FontAwesomeIcon icon={faHeart} />
                    </div>
                    <div className="story-content">
                      <h3>Vocación familiar</h3>
                      <p>
                        Fundado por una familia con vocación por el bienestar de las personas, Blanes nació como una casa de puertas abiertas, donde cada residente es tratado con cercanía, dignidad y profesionalismo. Sabemos que dejar a un ser querido en manos de otros no es una decisión fácil, por eso trabajamos cada día para ganarnos la confianza de las familias.
                      </p>
                    </div>
                  </div>
                  
                  <div className="story-item animate-item">
                    <div className="story-icon">
                      <FontAwesomeIcon icon={faHandshake} />
                    </div>
                    <div className="story-content">
                      <h3>Equipo comprometido</h3>
                      <p>
                        Contamos con un equipo capacitado y comprometido que combina experiencia médica, atención personalizada y un profundo sentido humano. Cada rincón de nuestra casa está pensado para generar un ambiente cálido, seguro y activo, donde los mayores puedan sentirse acompañados y valorados.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="about-quote">
                  <blockquote>
                    "Hoy, después de más de una década de trabajo, seguimos creciendo con el mismo compromiso: cuidar como en casa, y estar presentes como una familia extendida."
                  </blockquote>
                </div>
                
                <div className="about-actions">
                  <button className="cta-button primary" onClick={() => scrollToSection('contact')}>Hablá con nosotros</button>
                  <button className="cta-button secondary" onClick={() => scrollToSection('facilities')}>Ver instalaciones</button>
                </div>
              </div>
              
              <div className="about-visual">
                <div className="about-image-container">
                  <img src="/images/New-images/ResidencialBlanes-72.jpg" alt="Residencial Blanes" />

                </div>
                
                <div className="about-stats animate-on-scroll">
                  <div className="stat-item animate-item">
                    <div className="stat-number">10+</div>
                    <div className="stat-label">Años de experiencia</div>
                  </div>
                  <div className="stat-item animate-item">
                    <div className="stat-number">24/7</div>
                    <div className="stat-label">Atención continua</div>
                  </div>
                  <div className="stat-item animate-item">
                    <div className="stat-number">100%</div>
                    <div className="stat-label">Compromiso familiar</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="facilities" className="facilities">
          <div className="container">
            <h2 className="animate-on-scroll animate-item">Nuestras instalaciones</h2>
            <p className="facilities-intro animate-on-scroll animate-item">Residencial Blanes cuenta con espacios amplios, cómodos y adaptados a las necesidades de cada residente. Nuestro objetivo es que se sientan realmente en casa, con toda la seguridad y el confort necesarios.</p>
            <div className="facilities-features animate-on-scroll">
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faHome} className="feature-icon" />
                <span>Habitaciones privadas y compartidas</span>
              </div>
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faUtensils} className="feature-icon" />
                <span>Comedor con alimentos caseros y supervisión nutricional</span>
              </div>
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faTree} className="feature-icon" />
                <span>Espacios verdes y jardín interno</span>
              </div>
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faCouch} className="feature-icon" />
                <span>Salón de descanso y actividades recreativas</span>
              </div>
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faRestroom} className="feature-icon" />
                <span>Baños adaptados con accesibilidad total</span>
              </div>
              <div className="feature-item animate-item">
                <FontAwesomeIcon icon={faBed} className="feature-icon" />
                <span>Camas ortopédicas y mobiliario funcional</span>
              </div>
            </div>
            <div className="gallery-grid animate-on-scroll">
              {galleryImages.map((image, index) => (
                <div key={index} className="gallery-item animate-item" onClick={() => openModal(index)}>
                  <img src={image.src} alt={image.alt} />
                  <div className="gallery-overlay">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="zoom-icon" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="services">
          <div className="container">
            <div className="services-content">
              <div className="services-text animate-on-scroll">
                <h2 className="animate-item">Servicios que ofrecemos</h2>
                <p className="services-intro animate-item">En Residencial Blanes brindamos un cuidado integral que abarca lo físico, emocional y social, con un enfoque humano y personalizado.</p>
                <ul>
                  <li><FontAwesomeIcon icon={faUserMd} className="service-icon" /> Atención de enfermería 24 horas</li>
                  <li><FontAwesomeIcon icon={faStethoscope} className="service-icon" /> Supervisión médica periódica</li>
                  <li><FontAwesomeIcon icon={faUtensils} className="service-icon" /> Alimentación completa, casera y adaptada</li>
                  <li><FontAwesomeIcon icon={faPills} className="service-icon" /> Control de medicación diario</li>
                  <li><FontAwesomeIcon icon={faSoap} className="service-icon" /> Asistencia en higiene y cuidado personal</li>
                  <li><FontAwesomeIcon icon={faMusic} className="service-icon" /> Musicoterapia grupal</li>
                  <li><FontAwesomeIcon icon={faDumbbell} className="service-icon" /> Fisioterapia y gimnasia suave</li>
                  <li><FontAwesomeIcon icon={faPalette} className="service-icon" /> Actividades recreativas y de estimulación cognitiva</li>
                  <li><FontAwesomeIcon icon={faHeart} className="service-icon" /> Acompañamiento emocional y espiritual</li>
                  <li><FontAwesomeIcon icon={faShield} className="service-icon" /> Ambiente seguro y adaptado para todas las edades</li>
                </ul>
                <p className="services-quote">"Cuidamos a cada persona como nos gustaría que cuidaran a nuestros propios padres."</p>
              </div>
              <div className="services-image">
                <img src="/images/New-images/ResidencialBlanes-52.jpg" alt="Servicios" />
              </div>
            </div>
          </div>
        </section>

        <section id="testimonials" className="testimonials">
          <div className="container">
            <h2 className="animate-on-scroll animate-item">Testimonios</h2>
            <div className="testimonials-grid animate-on-scroll">
              <div className="testimonial-item animate-item">
                <p>"Mi madre volvió a sonreír desde que está en Blanes. No solo la cuidan bien, la hacen sentir querida."</p>
                <cite>— Ana, hija de una residente</cite>
              </div>
              <div className="testimonial-item animate-item">
                <p>"La tranquilidad que tengo sabiendo que mi padre está en buenas manos no tiene precio."</p>
                <cite>— Gustavo, familiar</cite>
              </div>
              <div className="testimonial-item animate-item">
                <p>"El equipo es excelente, muy humano y dedicado. Se nota que trabajan con el corazón."</p>
                <cite>— María Elena, sobrina</cite>
              </div>
            </div>
          </div>
        </section>

        <section className="why-choose-us">
          <div className="container">
            <h2 className="animate-on-scroll animate-item">¿Por qué elegirnos?</h2>
            <h3 className="animate-on-scroll animate-item">Porque no es solo un lugar: es un hogar.</h3>
            <div className="benefits-grid animate-on-scroll">
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Trato cálido, familiar y cercano</span>
              </div>
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Personal capacitado y comprometido</span>
              </div>
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Comunicación constante con la familia</span>
              </div>
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Instalaciones seguras y adaptadas</span>
              </div>
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Más de 10 años de experiencia cuidando con amor</span>
              </div>
              <div className="benefit-item animate-item">
                <FontAwesomeIcon icon={faCheckCircle} className="benefit-icon" />
                <span>Actividades recreativas y estimulación cognitiva</span>
              </div>
            </div>
            <p className="tagline">"En Blanes no cuidamos pacientes, cuidamos personas."</p>
          </div>
        </section>

        <section id="faq" className="faq">
          <div className="container">
            <h2 className="animate-on-scroll animate-item">Preguntas frecuentes</h2>
            <div className="faq-content animate-on-scroll">
              <div className="faq-text">
                <div className="faq-item animate-item">
                  <h3>¿Puedo visitar antes de decidir?</h3>
                  <p>Sí, podés agendar una visita en el día y horario que te quede más cómodo. Nos encantará recibirte.</p>
                </div>
                <div className="faq-item animate-item">
                  <h3>¿Qué tipo de personas pueden ingresar?</h3>
                  <p>Recibimos adultos mayores autoválidos o con dependencia leve o moderada. Adaptamos el cuidado a cada caso.</p>
                </div>
                <div className="faq-item animate-item">
                  <h3>¿Puedo visitar a mi familiar todos los días?</h3>
                  <p>Sí, nuestras visitas son abiertas dentro del horario definido, con mucha flexibilidad y respeto por los vínculos.</p>
                </div>
                <div className="faq-item animate-item">
                  <h3>¿Qué incluye la estadía?</h3>
                  <p>Incluye alojamiento, 4 comidas diarias, higiene, medicación, atención de salud y actividades recreativas.</p>
                </div>
                <div className="faq-item animate-item">
                  <h3>¿Tienen médico en el lugar?</h3>
                  <p>Contamos con control médico regular y asistencia de enfermería permanente. Derivamos a especialistas cuando es necesario.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="contact">
          <div className="contact-background">
            <img src="/images/9.jpg" alt="Contact Background" className="contact-bg-image" />
            <div className="contact-overlay"></div>
          </div>
          <div className="container">
            <div className="contact-header animate-on-scroll">
              <h2 className="animate-item">¿Querés conocer Residencial Blanes?</h2>
              <p className="contact-intro animate-item">Te invitamos a visitarnos y descubrir por qué tantas familias confían en nosotros.</p>
            </div>
            
            <div className="contact-content">
              <div className="contact-info animate-on-scroll">
                <div className="contact-card animate-item">
                  <div className="contact-icon"><FontAwesomeIcon icon={faLocationDot} /></div>
                  <div className="contact-details">
                    <h4>Dirección</h4>
                    <p>Juan Manuel Blanes 965 - Montevideo</p>
                  </div>
                </div>
                
                <div className="contact-card animate-item">
                  <div className="contact-icon"><FontAwesomeIcon icon={faPhone} /></div>
                  <div className="contact-details">
                    <h4>Teléfono / WhatsApp</h4>
                    <p>+598 99 757 138</p>
                  </div>
                </div>
                
                <div className="contact-card animate-item">
                  <div className="contact-icon"><FontAwesomeIcon icon={faClock} /></div>
                  <div className="contact-details">
                    <h4>Horarios de atención</h4>
                    <p>Lunes a sábado de 9 a 18 hs</p>
                  </div>
                </div>
                
                <div className="contact-card animate-item">
                  <div className="contact-icon"><FontAwesomeIcon icon={faEnvelope} /></div>
                  <div className="contact-details">
                    <h4>Email</h4>
                    <p>residencialblanes@gmail.com</p>
                  </div>
                </div>
                
                <div className="contact-cta animate-item">
                  <h3>¡Agendá tu visita hoy!</h3>
                  <p>Conocé nuestras instalaciones y conversá con nuestro equipo sin compromiso.</p>
                </div>
              </div>
              
              <div className="contact-form-container">
                <form className="contact-form" onSubmit={handleSubmit}>
                  <h3>Envianos tu consulta</h3>
                  
                  {submitStatus === 'success' && (
                    <div className="form-message success">
                      <span><FontAwesomeIcon icon={faCheckCircle} /> ¡Gracias! Tu consulta fue enviada exitosamente. Nos pondremos en contacto contigo pronto.</span>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="form-message error">
                      <span><FontAwesomeIcon icon={faTimes} /> Hubo un error al enviar tu consulta. Por favor, intentá nuevamente.</span>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <input 
                      type="text" 
                      name="name"
                      placeholder="Nombre completo" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <input 
                      type="tel" 
                      name="phone"
                      placeholder="Teléfono" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <textarea 
                      name="message"
                      placeholder="Cuéntanos sobre tu consulta..." 
                      rows="5" 
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>
                  <button 
                    type="submit" 
                    className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span>Enviando...</span>
                        <FontAwesomeIcon icon={faSpinner} className="btn-icon fa-spin" />
                      </>
                    ) : (
                      <>
                        <span>Agendá tu visita</span>
                        <FontAwesomeIcon icon={faArrow} className="btn-icon" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <h3>Residencial Blanes</h3>
              <p><FontAwesomeIcon icon={faLocationDot} /> Juan Manuel Blanes 965 - Montevideo</p>
              <p><FontAwesomeIcon icon={faPhone} /> Teléfono / WhatsApp: +598 99 757 138</p>
              <p><FontAwesomeIcon icon={faClock} /> Lunes a sábado de 9 a 18 hs</p>
            </div>
            <div className="footer-contact">
              <p>&copy; 2025 Residencial Blanes. Todos los derechos reservados.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      {showCookieBanner && (
        <div className="cookie-banner">
          <div className="cookie-content">
            <div className="cookie-icon">
              <FontAwesomeIcon icon={faCookie} />
            </div>
            <div className="cookie-text">
              <h4>Uso de cookies</h4>
              <p>
                Utilizamos cookies para mejorar tu experiencia en nuestro sitio web, 
                analizar el tráfico y personalizar el contenido. Al continuar navegando, 
                aceptas nuestro uso de cookies.
              </p>
            </div>
            <div className="cookie-actions">
              <button className="cookie-btn secondary" onClick={openTermsModal}>
                <FontAwesomeIcon icon={faFileContract} /> Ver términos
              </button>
              <button className="cookie-btn reject" onClick={rejectCookies}>
                Rechazar
              </button>
              <button className="cookie-btn accept" onClick={acceptCookies}>
                <FontAwesomeIcon icon={faCheckCircle} /> Aceptar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="modal-overlay terms-modal-overlay" onClick={closeTermsModal}>
          <div className="modal-content terms-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="terms-header">
              <h2><FontAwesomeIcon icon={faFileContract} /> Términos y Condiciones de Uso de Cookies</h2>
              <button className="modal-close" onClick={closeTermsModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="terms-content">
              <div className="terms-section">
                <h3><FontAwesomeIcon icon={faCookie} /> ¿Qué son las cookies?</h3>
                <p>
                  Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo 
                  cuando visitas nuestro sitio web. Nos ayudan a recordar tus preferencias y 
                  mejorar tu experiencia de navegación.
                </p>
              </div>

              <div className="terms-section">
                <h3><FontAwesomeIcon icon={faGear} /> Tipos de cookies que utilizamos</h3>
                <div className="cookie-types">
                  <div className="cookie-type">
                    <h4>Cookies esenciales</h4>
                    <p>Necesarias para el funcionamiento básico del sitio web, como la navegación y el acceso a áreas seguras.</p>
                  </div>
                  <div className="cookie-type">
                    <h4>Cookies de funcionalidad</h4>
                    <p>Permiten que el sitio web recuerde las elecciones que haces y proporcione características mejoradas y más personalizadas.</p>
                  </div>
                  <div className="cookie-type">
                    <h4>Cookies de análisis</h4>
                    <p>Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web, proporcionando información sobre las áreas visitadas.</p>
                  </div>
                </div>
              </div>

              <div className="terms-section">
                <h3><FontAwesomeIcon icon={faShield} /> Tu control sobre las cookies</h3>
                <p>
                  Puedes controlar y/o eliminar las cookies como desees. Puedes eliminar todas 
                  las cookies que ya están en tu dispositivo y configurar la mayoría de los 
                  navegadores para evitar que se coloquen.
                </p>
              </div>

              <div className="terms-section">
                <h3><FontAwesomeIcon icon={faHeart} /> Nuestro compromiso</h3>
                <p>
                  En Residencial Blanes, respetamos tu privacidad y nos comprometemos a ser 
                  transparentes sobre cómo utilizamos las cookies. Solo utilizamos cookies 
                  que mejoran tu experiencia y nos ayudan a brindarte un mejor servicio.
                </p>
              </div>

              <div className="terms-section contact-info">
                <h3><FontAwesomeIcon icon={faEnvelope} /> Contacto</h3>
                <p>
                  Si tienes alguna pregunta sobre nuestro uso de cookies, puedes contactarnos en:
                </p>
                <p><strong>Email:</strong> residencialblanes@gmail.com</p>
                <p><strong>Teléfono:</strong> +598 99 757 138</p>
                <p><strong>Dirección:</strong> Juan Manuel Blanes 965 - Montevideo, Uruguay</p>
              </div>
            </div>
            
            <div className="terms-footer">
              <button className="cookie-btn reject" onClick={() => { closeTermsModal(); rejectCookies(); }}>
                Rechazar cookies
              </button>
              <button className="cookie-btn accept" onClick={() => { closeTermsModal(); acceptCookies(); }}>
                <FontAwesomeIcon icon={faCheckCircle} /> Aceptar cookies
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /></button>
            <button className="modal-nav modal-prev" onClick={prevImage}><FontAwesomeIcon icon={faArrowLeft} /></button>
            <button className="modal-nav modal-next" onClick={nextImage}><FontAwesomeIcon icon={faArrowRight} /></button>
            <div className="modal-image-container">
              <img 
                src={galleryImages[currentImageIndex].src} 
                alt={galleryImages[currentImageIndex].alt} 
                className="modal-image"
              />
            </div>
            <div className="modal-info">
              <span className="modal-counter">
                {currentImageIndex + 1} / {galleryImages.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Button (Fixed position) */}
      {cookieConsent !== null && (
        <button
          className="cookie-settings-btn"
          onClick={() => setShowCookieBanner(true)}
          title="Configurar cookies"
        >
          <FontAwesomeIcon icon={faGear} />
        </button>
      )}

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/59899757138?text=Hola,%20me%20gustaría%20obtener%20más%20información%20sobre%20Residencial%20Blanes"
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        title="Contactar por WhatsApp"
      >
        <FontAwesomeIcon icon={faWhatsapp} />
      </a>
    </div>
  );
}

export default App;